/**
 * Knowledge Canvases — typed endpoint helpers.
 *
 * Ports v1 ``ando-frontend/app/api/canvases.ts`` (1:1) onto the v2 ``apiClient``
 * (cookie-auth + 401 refresh-once). The BE surface is in
 * ``ando-agent/src/api/v1/endpoints/canvases.py``:
 *
 *   GET    /canvases                                  list (entity_type/scope/status/...)
 *   GET    /canvases/by-slug/{slug}?scope=...         resolve by slug (walks user→org→global)
 *   GET    /canvases/{id}                             resolve by uuid
 *   POST   /canvases/compile/{entity_id}              create + start compile workflow
 *   POST   /canvases/{id}/regenerate                  re-run compile
 *   PATCH  /canvases/{id}/visibility                  flip scope
 *   DELETE /canvases/{id}                             soft-delete
 *   POST   /canvases/{id}/archive                     archive (idempotent)
 *   DELETE /canvases/{id}/archive                     unarchive (idempotent)
 *   GET    /canvases/{id}/compile-runs?limit=N        audit trail
 *   GET    /canvases/{id}/citations/{claim_id}        single citation preview
 *
 * Wire shapes are snake_case (BE-canonical) — see v1
 * ``ando-frontend/app/types/canvases.ts`` for the type-by-type rationale.
 */

import { apiClient } from "../ApiClient.js";

// ---------------------------------------------------------------------------
// Wire types — match the BE shape (snake_case). Kept in this module rather
// than @ando/core/domain because canvases are isolated to one feature slice
// for now; promote to /domain if a second consumer appears.
// ---------------------------------------------------------------------------

export type CanvasScope = "user" | "org" | "global";

export type CanvasEntityType =
  | "person"
  | "organization"
  | "project"
  | "concept"
  | "document"
  | "location"
  | "other";

export type CanvasCompileStatus =
  | "pending"
  | "compiling"
  | "ready"
  | "failed"
  | "stale"
  | "archived";

/** Server-side filter for the Active / Archived tabs. */
export type CanvasStatusFilter = "active" | "archived" | "all";

export type CanvasSourceType =
  | "email"
  | "document"
  | "chat"
  | "database"
  | "other";

export interface CanvasSection {
  id: string;
  title: string;
  level: number;
  anchor: string;
}

export interface Canvas {
  id: string;
  tenant_id: string;
  scope: CanvasScope;
  owner_user_id: string | null;
  org_id: string | null;
  entity_id: string;
  entity_type: CanvasEntityType;
  slug: string;
  title: string;
  subtitle: string | null;
  content_md: string;
  sections: CanvasSection[];
  citation_map: Record<string, unknown>;
  source_ids: string[];
  source_count: number;
  mention_count: number;
  confidence_score: number;
  stale_sources_count: number;
  compile_status: CanvasCompileStatus;
  last_compile_error: string | null;
  compiled_at: string | null;
  compile_version: number;
  created_at: string;
  updated_at: string;
}

export interface CanvasListResponse {
  items: Canvas[];
  total: number;
}

export interface CanvasListParams {
  entity_type?: CanvasEntityType;
  scope?: CanvasScope;
  search?: string;
  mention_min?: number;
  limit?: number;
  offset?: number;
  status?: CanvasStatusFilter;
}

export interface CanvasCompileRun {
  id: string;
  canvas_id: string;
  workflow_id: string | null;
  trigger: "manual" | "scheduled" | "event" | "regenerate";
  triggered_by_user_id: string | null;
  status: "running" | "success" | "failed" | "cancelled";
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  tokens_input: number;
  tokens_output: number;
  cost_usd: number;
  sources_count: number;
  sections_count: number;
  error_message: string | null;
}

// ---------------------------------------------------------------------------
// Query-string helper — single source of truth, matches v1
// (``buildQuery`` in ``app/api/canvases.ts``). Skips ``undefined``/``null``/``""``.
// ---------------------------------------------------------------------------

function buildQuery(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== "",
  );
  if (entries.length === 0) return "";
  const search = new URLSearchParams();
  for (const [k, v] of entries) {
    search.append(k, String(v));
  }
  return `?${search.toString()}`;
}

const BASE = "/canvases";

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export async function listCanvases(
  params: CanvasListParams = {},
): Promise<CanvasListResponse> {
  return apiClient.get<CanvasListResponse>(
    `${BASE}${buildQuery(params as Record<string, unknown>)}`,
  );
}

export async function getCanvasById(id: string): Promise<Canvas> {
  return apiClient.get<Canvas>(`${BASE}/${encodeURIComponent(id)}`);
}

export async function getCanvasBySlug(
  slug: string,
  scope?: CanvasScope,
): Promise<Canvas> {
  const qs = scope ? `?scope=${encodeURIComponent(scope)}` : "";
  return apiClient.get<Canvas>(`${BASE}/by-slug/${encodeURIComponent(slug)}${qs}`);
}

export async function listCompileRuns(
  id: string,
  limit = 20,
): Promise<CanvasCompileRun[]> {
  return apiClient.get<CanvasCompileRun[]>(
    `${BASE}/${encodeURIComponent(id)}/compile-runs?limit=${limit}`,
  );
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

export async function compileCanvas(
  entityId: string,
  body: { scope?: CanvasScope; title?: string } = {},
): Promise<Canvas> {
  return apiClient.post<Canvas>(
    `${BASE}/compile/${encodeURIComponent(entityId)}`,
    body,
  );
}

export async function regenerateCanvas(
  id: string,
  force = false,
): Promise<Canvas> {
  return apiClient.post<Canvas>(
    `${BASE}/${encodeURIComponent(id)}/regenerate`,
    { force },
  );
}

export async function updateCanvasVisibility(
  id: string,
  newScope: CanvasScope,
): Promise<Canvas> {
  return apiClient.patch<Canvas>(
    `${BASE}/${encodeURIComponent(id)}/visibility`,
    { new_scope: newScope },
  );
}

export async function deleteCanvas(id: string): Promise<void> {
  await apiClient.delete<void>(`${BASE}/${encodeURIComponent(id)}`);
}

export async function archiveCanvas(id: string): Promise<Canvas> {
  return apiClient.post<Canvas>(
    `${BASE}/${encodeURIComponent(id)}/archive`,
    {},
  );
}

export async function unarchiveCanvas(id: string): Promise<Canvas> {
  return apiClient.delete<Canvas>(`${BASE}/${encodeURIComponent(id)}/archive`);
}
